import { jsonPost } from '../json-request';
import type { ValidatedApiClient } from '../types/api-client';
import {
    relatedUserIdeasSchema,
    type UserIdeaApplicationArea,
    type UserIdeaFilters,
    type UserIdeaType,
    userIdeaMutationSchema,
    userIdeaPageSchema,
    userIdeaResponseSchema,
} from './schema';

const USER_IDEAS_PATH = '/v3/user_ideas';

export const createUserIdeasApi = ({ validatedFetch }: ValidatedApiClient) => {
    const getIdeas = (
        gameId: string | undefined,
        filters: UserIdeaFilters,
        offset = 0,
        signal?: AbortSignal,
        locale?: string,
    ) => {
        const params = new URLSearchParams({
            scopeType: gameId || filters.gameIds ? 'game' : 'all',
            limit: '20',
            offset: String(offset),
            sort: filters.sort,
        });
        if (gameId) params.set('gameId', gameId);
        if (locale) params.set('locale', locale);
        for (const id of filters.gameIds ?? []) params.append('gameIds[]', id);
        if (filters.status) params.set('status', filters.status);
        if (filters.type) params.set('type', filters.type);
        if (filters.search) params.set('search', filters.search);
        if (filters.filter) params.set('filter', filters.filter);
        return validatedFetch(userIdeaPageSchema, USER_IDEAS_PATH, params, { signal });
    };
    return {
        getGameIdeas: (gameId: string, filters: UserIdeaFilters, offset = 0, signal?: AbortSignal, locale?: string) =>
            getIdeas(gameId, filters, offset, signal, locale),
        getIdeas: (filters: UserIdeaFilters, offset = 0, signal?: AbortSignal, locale?: string) =>
            getIdeas(undefined, filters, offset, signal, locale),
        getRelatedGameIdeas: (
            gameId: string,
            idea: string,
            type: UserIdeaType,
            signal?: AbortSignal,
            locale?: string,
        ) =>
            validatedFetch(
                relatedUserIdeasSchema,
                `${USER_IDEAS_PATH}/related`,
                new URLSearchParams(locale ? { locale } : {}),
                {
                    ...jsonPost({ scopeType: 'game', gameId, type, idea }),
                    signal,
                },
            ),
        getRelatedApplicationIdeas: (
            applicationArea: UserIdeaApplicationArea,
            idea: string,
            type: UserIdeaType,
            signal?: AbortSignal,
            locale?: string,
        ) =>
            validatedFetch(
                relatedUserIdeasSchema,
                `${USER_IDEAS_PATH}/related`,
                new URLSearchParams(locale ? { locale } : {}),
                {
                    ...jsonPost({ scopeType: 'application', applicationArea, type, idea }),
                    signal,
                },
            ),
        submitApplicationIdea: (
            applicationArea: UserIdeaApplicationArea,
            idea: string,
            details: string,
            type: UserIdeaType,
        ) =>
            validatedFetch(
                userIdeaMutationSchema,
                USER_IDEAS_PATH,
                new URLSearchParams(),
                jsonPost({ scopeType: 'application', applicationArea, type, idea, details }),
            ),
        submitGameIdea: (gameId: string, idea: string, details: string, type: UserIdeaType) =>
            validatedFetch(
                userIdeaMutationSchema,
                USER_IDEAS_PATH,
                new URLSearchParams(),
                jsonPost({ scopeType: 'game', gameId, type, idea, details }),
            ),
        getModIdea: (id: number, signal?: AbortSignal, locale?: string) =>
            validatedFetch(
                userIdeaResponseSchema,
                `${USER_IDEAS_PATH}/${id}`,
                new URLSearchParams(locale ? { locale } : {}),
                { signal },
            ),
        boostModIdea: (id: number) =>
            validatedFetch(
                userIdeaMutationSchema,
                `${USER_IDEAS_PATH}/${id}/boost`,
                new URLSearchParams(),
                jsonPost({}),
            ),
    };
};
